import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, MapPin, Calendar, Users } from "lucide-react";
import { Link } from "react-router-dom";
import heroLion from "@/assets/hero-lion-optimized.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { useTheme } from "@/components/ThemeProvider";
import { Helmet } from "react-helmet-async";
import satibSafeLogo from "@/assets/partners/satib-safe.png";
import satsaLogo from "@/assets/partners/satsa.gif";
import fgasaLogo from "@/assets/partners/fgasa.png";
import safariBookingsLogo from "@/assets/partners/safaribookings.png";
import namibiaTourismBoardLogo from "@/assets/partners/namibia-tourism-board.png";

const Index = () => {
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  // Determine actual theme (resolve 'system' to 'light' or 'dark')
  const actualTheme =
    theme === "system" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme;

  // Fetch featured tours
  const { data: tours } = useQuery({
    queryKey: ["featured-tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .eq("is_featured", true)
        .eq("is_active", true)
        .limit(3);

      if (error) throw error;
      return data;
    },
  });

  // Reviews feature temporarily disabled (table not yet implemented)
  const reviews = [];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    name: "Shikwaru Adventures",
    description: "African safari tour operator specializing in Namibia, Botswana, and Zimbabwe tours",
    url: "https://shikwaru-adventures.com",
    logo: "https://shikwaru-adventures.com/og-image.jpg",
    image: "https://shikwaru-adventures.com/og-image.jpg",
    address: {
      "@type": "PostalAddress",
      addressLocality: "1 Paarl lane Johannesburg",
      addressCountry: "South Africa",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+27 73 2563514",
      contactType: "Customer Service",
      email: "info@shikwaru-adventures.com",
      availableLanguage: ["English", "Spanish", "French"],
    },
    areaServed: ["Namibia", "Botswana", "Zimbabwe", "South Africa", "Zambia"],
    priceRange: "$$-$$$",
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <script type="text/javascript" src="https://grwapi.net/widget.min.js" async />
      </Helmet>
      <SEO
        title="Shikwaru Adventures | African Safari Tours South Africa, Namibia, Botswana & Zimbabwe"
        description="Discover unforgettable African safaris with 20+ years expertise. Custom tours to Kruger Park, Okavango Delta, Victoria Falls & Namibian deserts. Camping, mid-range & luxury options. Multilingual guides (Spanish, French, English). Book your dream safari adventure now!"
        keywords="African safari tours, Namibia safari packages, Botswana Okavango Delta tours, Victoria Falls Zimbabwe, Kruger Park safari South Africa, camping safari Africa, luxury African safari, custom safari tours, multilingual safari guides, southern Africa tours, wildlife safari adventures, Garden Route safari, zebra migration Botswana"
        structuredData={structuredData}
        canonical="/"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section
          className="relative h-screen flex items-center justify-center bg-cover bg-[30%_center] md:bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${heroLion})`,
          }}
        >
          <div className="text-center text-white z-10 px-4">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">{t("hero.title")}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto">{t("hero.subtitle")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/tours">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8">
                  {t("hero.exploreTours")}
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20"
                >
                  {t("hero.contactUs")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Welcome Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-primary font-semibold mb-2">WELCOME</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{t("welcome.title")}</h2>
            </div>

            <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground leading-relaxed">
              {language === "en" && (
                <>
                  <p>
                    Shikwaru Adventures is dedicated to creating exceptional travel experiences, offering a range of
                    private tours across Southern Africa for individuals and small groups Whether you prefer mid-range
                    comfort or luxury travel, we tailor every journey with care, efficiency, and personal attention.
                  </p>
                  <p>
                    With over 20 years of industry expertise, we are an owner-run company that takes pride in delivering
                    warm, personalised service. Our highly knowledgeable guides are passionate about sharing the
                    region's wildlife, culture, and rich heritage.
                  </p>
                  <p>
                    We proudly partner with local communities and service providers, ensuring your journey supports and
                    uplifts the people and places you visit, strengthening local economies and preserving authentic
                    African experiences.
                  </p>
                  <p>
                    Tours are offered in English, Spanish, and French, ensuring your adventure is immersive,
                    comfortable, and unforgettable.
                  </p>
                </>
              )}

              {language === "es" && (
                <>
                  <p>
                    Shikwaru Adventures está dedicada a crear experiencias de viaje excepcionales, ofreciendo una
                    variedad de tours privados por el sur de África para individuos y pequeños grupos. Ya sea que
                    prefieras comodidad de categoría media o lujo, diseñamos cada viaje con cuidado, eficiencia y
                    atención personalizada.
                  </p>
                  <p>
                    Con más de 20 años de experiencia en la industria, somos una empresa dirigida por sus propietarios,
                    orgullosa de ofrecer un servicio cálido y personalizado. Nuestros guías altamente capacitados están
                    apasionados por compartir la vida salvaje, la cultura y el patrimonio de esta fascinante región.
                  </p>
                  <p>
                    Trabajamos con comunidades y proveedores locales, garantizando que tu viaje apoye y beneficie a las
                    personas y lugares que visitas, impulsando la economía local y preservando experiencias auténticas
                    africanas.
                  </p>
                  <p>
                    Ofrecemos tours en inglés, español y francés, para que tu aventura sea inmersiva, cómoda e
                    inolvidable.
                  </p>
                </>
              )}

              {language === "fr" && (
                <>
                  <p>
                    Shikwaru Adventures est spécialisée dans la création d'expériences de voyage exceptionnelles, en
                    proposant des circuits privés à travers l'Afrique australe pour individus et petits groupes —
                    toujours avec un excellent rapport qualité-prix. Que vous préfériez un confort milieu de gamme ou un
                    séjour luxueux, nous concevons chaque voyage avec soin, efficacité et attention personnalisée.
                  </p>
                  <p>
                    Avec plus de 20 ans d'expérience dans le secteur, nous sommes une entreprise dirigée par ses
                    propriétaires, fière d'offrir un service chaleureux et personnalisé. Nos guides expérimentés sont
                    passionnés par le partage de la faune, de la culture et du riche patrimoine de cette magnifique
                    région.
                  </p>
                  <p>
                    Nous collaborons avec les communautés et prestataires locaux, afin que votre voyage soutienne et
                    valorise les populations et les lieux que vous découvrez, contribuant à l'économie locale et à la
                    préservation d'expériences africaines authentiques.
                  </p>
                  <p>
                    Nous proposons des circuits en anglais, espagnol et français, pour que votre aventure soit
                    immersive, confortable et inoubliable.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Featured Tours */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">{t("tours.title")}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t("tours.subtitle")}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {tours?.map((tour) => {
                const name =
                  language === "es"
                    ? tour.name
                    : language === "en"
                      ? tour.name_en || tour.name
                      : tour.name_fr || tour.name;
                const description =
                  language === "es"
                    ? tour.short_description
                    : language === "en"
                      ? tour.short_description_en || tour.short_description
                      : tour.short_description_fr || tour.short_description;
                const destination =
                  language === "es"
                    ? tour.destination
                    : language === "en"
                      ? tour.destination_en || tour.destination
                      : tour.destination_fr || tour.destination;
                const imageUrl =
                  tour.gallery_images && tour.gallery_images.length > 0 ? tour.gallery_images[0] : tour.image_url;

                return (
                  <Card key={tour.id} className="overflow-hidden hover:shadow-xl transition-all">
                    <img src={imageUrl} alt={name} className="w-full h-64 object-cover" />
                    <CardHeader>
                      <CardTitle>{name}</CardTitle>
                      <CardDescription>{description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin size={16} className="mr-2 text-primary" />
                        {destination}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar size={16} className="mr-2 text-primary" />
                        {tour.duration_days} {t("tours.days")}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Users size={16} className="mr-2 text-primary" />
                        {tour.min_group_size && tour.min_group_size !== tour.max_group_size
                          ? `${tour.min_group_size}-${tour.max_group_size}`
                          : tour.max_group_size || 12}{" "}
                        {t("common.people")}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t">
                        <span className="text-2xl font-bold text-primary">${tour.price_per_person}</span>
                        <span className="text-sm text-muted-foreground">{t("tours.perPerson")}</span>
                      </div>
                      <Link to="/tours" className="block">
                        <Button className="w-full">{t("tours.viewDetails")}</Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to="/tours">
                <Button size="lg" variant="outline">
                  {t("tours.viewAll")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-16 bg-background border-y border-border">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">{t("partners.title")}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t("partners.subtitle")}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {[
                { logo: satibSafeLogo, name: "SATIB SAFE" },
                { logo: satsaLogo, name: "SATSA" },
                { logo: fgasaLogo, name: "FGASA" },
                { logo: safariBookingsLogo, name: "SafariBookings" },
                { logo: namibiaTourismBoardLogo, name: "Namibia Tourism Board" },
              ].map((partner, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:scale-105 group animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-20 w-auto object-contain mb-4 grayscale-[30%] group-hover:grayscale-0 transition-all duration-300"
                    title={partner.name}
                  />
                  <p className="text-xs text-center font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    {partner.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">{t("reviews.title")}</h2>
              <p className="text-muted-foreground">{t("reviews.subtitle")}</p>
            </div>

            <div className="flex flex-col md:flex-row justify-center items-center gap-8 py-2 px-2">
              {/* SafariBookings Widget */}
              <div className="flex justify-center items-center">
                <a href="https://www.safaribookings.com/reviews/p3348" target="_blank" rel="nofollow">
                  <img
                    src="https://s3.amazonaws.com/z_437er23a/images/p3348-1.png"
                    alt="Reviews about Shikwaru Adventures"
                    title="Reviews about Shikwaru Adventures"
                    className="max-w-full h-auto"
                  />
                </a>
              </div>

              {/* GRW Reviews Widget */}
              <div className="flex justify-center items-center">
                <div
                  className="review-widget_net"
                  data-uuid="52006a57-66cf-48e2-9c29-c45644ad91f5"
                  data-template="10"
                  data-lang={language}
                  data-theme={actualTheme}
                ></div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
